import { useSelector } from 'react-redux';
import styled from 'styled-components';

import { selectUserProjectId } from 'core/userContext';
import {
  selectIsGlobalSmsFirstPartyUIFeatureFlagOn,
  selectIsSMSContactCardUIOn,
} from 'core/userContext/selectors';
import { OptOutType } from 'modules/globalSms/types';
import { SMSContentDefinition } from 'modules/graphql/types';
import { HeadStyleMobile } from 'modules/secureIframe/HeadStyleMobile';
import { usePreviewWithDataContext } from 'modules/templateEditor/context/PreviewWithDataContext';
import { useSMSUserData } from 'modules/templateEditor/hooks/useSMSUserData';
import { BUTTON_VARIANTS } from 'ui/button';
import { Android24, AnimatedRefreshIcon, Apple24, InfoFill } from 'ui/icons';
import { SegmentedControl } from 'ui/segmentedControl';
import { BASIC_SELECT_SIZES, BasicSelect } from 'ui/selects';
import { colors, Label, spacing } from 'ui/theme';
import { Toggle } from 'ui/toggle';
import { HelpTooltip, Tooltip } from 'ui/tooltip';
import { capitalizeFirstLetter } from 'utils/helpers';
import { useLocalStorage } from 'utils/hooks/useLocalStorage';

import {
  PreviewContent,
  PreviewControls,
  PreviewFooter,
  PreviewMessage,
  PreviewRefreshButton,
  PreviewShortenAndTrackContainer,
  shortenLinkInMessage,
} from '../../common';
import {
  PREVIEWER_CONTENT,
  TEMPLATE_EDITOR_SMS_PREVIEW_CHAR_COUNT,
  TEMPLATE_EDITOR_SMS_PREVIEW_DATA_MESSAGE,
  TEMPLATE_EDITOR_SMS_PREVIEW_PANE_ID,
  TEMPLATE_EDITOR_SMS_PREVIEW_REFRESH_BUTTON,
  TEMPLATE_EDITOR_SMS_PREVIEW_SEG_COUNT,
  TEMPLATE_EDITOR_SMS_PREVIEW_SHORTEN_TRACK,
  TEMPLATE_EDITOR_SMS_PREVIEW_TYPE_SELECTOR,
} from '../constants';
import { Preview } from './Preview';

import { PreviewHeader } from '../../common/PreviewHeader';
import { displayContent } from '../../common/content';
import { OS, PreviewType, TemplateEditViews } from '../../common/types';
import {
  getCharLimitPerMsg,
  getGSM7CharacterLength,
  getMsgCount,
  hasHandlebars,
} from '../../common/utils';
import { useEditableFieldsContext } from '../../context/EditableFieldsContext';
import { useQueryParamContext } from '../../context/QueryParamContext';
import { useSMSLinkShortening } from '../context/SMSLinkShorteningContext';
import { useSMSPreviewContext } from '../context/SMSPreviewContext';
import { previewTypeOptions } from '../utils';
import { previewInfoMessage, SMSPreviewContainer } from './styles';

const StyledSelect = styled(BasicSelect)`
  #template-editor-sms-preview-type-selector-contactCard[aria-disabled='true'] {
    background: none;
  }
`;

const OptOutControls = styled.div`
  display: flex;
  flex-direction: row;
  gap: ${spacing.half};
  align-items: center;
  margin-bottom: ${spacing.half};
  justify-content: center;
`;

const OptOutLabel = styled(Label)`
  margin-bottom: 0;
`;

const Separator = styled.span`
  display: inline-block;
  color: ${colors.grey35};
  padding: 0 ${spacing.half};
`;

export const PreviewPane = () => {
  const { view } = useQueryParamContext();
  const {
    hasAlphanumericSupport,
    isLoading: isRefreshing,
    messageContent,
    onRefreshPreview,
    optOutType,
    previewContactCard,
    setOptOutType,
    optInMessageIsShown,
    setOptInMessageIsShown,
    fullSmsMessage,
  } = useSMSPreviewContext();
  const { liveTemplate, hasContent } = useEditableFieldsContext();
  const { isSMSLinkShorteningEnabled, shortlinkLength, defaultSmsLinkShorteningDomain } =
    useSMSLinkShortening();
  const isGlobalSmsEnabled = useSelector(selectIsGlobalSmsFirstPartyUIFeatureFlagOn);

  const [selectedOs, setSelectedOs] = useLocalStorage<OS>('smsPreviewOs', OS.ios);
  const [selectedPreviewType, setSelectedPreviewType] = useLocalStorage<PreviewType>(
    'smsPreviewType',
    PreviewType.notification,
  );
  const { userDataIsValid, userData } = usePreviewWithDataContext();
  const { userIsUSOrCanada } = useSMSUserData(userDataIsValid, userData);
  const smsData = (liveTemplate?.mediumData || {}) as SMSContentDefinition;
  const isContactCardSelected =
    smsData?.verifiedContactCardId && smsData.verifiedContactCardId > -1;
  const isDisabled = !hasContent && !isContactCardSelected;
  const isPreviewWithData = view === TemplateEditViews.preview;
  const message = isPreviewWithData ? messageContent : fullSmsMessage;
  const hasDataParamsInMsg = !isPreviewWithData && hasHandlebars(fullSmsMessage);
  const typeOfSegment = smsData.imageUrl || isContactCardSelected ? `MMS` : `SMS`;
  const smsContactCardEnabled = useSelector(selectIsSMSContactCardUIOn);
  const projectId = useSelector(selectUserProjectId);
  const messageMaybeWithShortenLink =
    shortenLinkInMessage(
      message,
      smsData.trackingDomain ?? defaultSmsLinkShorteningDomain ?? '',
      projectId,
      shortlinkLength,
      isSMSLinkShorteningEnabled,
    ) || '';
  const characterCount = getGSM7CharacterLength(messageMaybeWithShortenLink);
  const perMessageLimit = getCharLimitPerMsg(
    messageMaybeWithShortenLink,
    getGSM7CharacterLength(messageMaybeWithShortenLink),
  );
  const segmentCount = getMsgCount(
    messageMaybeWithShortenLink,
    getGSM7CharacterLength(messageMaybeWithShortenLink),
  );

  const shouldShowMMSLinkDisclaimer =
    isPreviewWithData &&
    isGlobalSmsEnabled &&
    !userIsUSOrCanada &&
    (smsData.imageUrl || previewContactCard);

  const osSelectOptions = [
    {
      disabled: isDisabled,
      Icon: Apple24,
      key: OS.ios,
      value: OS.ios,
    },
    {
      disabled: isDisabled,
      Icon: Android24,
      key: OS.android,
      value: OS.android,
    },
  ];

  const optOutTypeSelectOptions = [
    {
      name: capitalizeFirstLetter(OptOutType.ALPHANUMERIC),
      value: OptOutType.ALPHANUMERIC,
    },
    {
      name: capitalizeFirstLetter(OptOutType.NUMERIC),
      value: OptOutType.NUMERIC,
    },
  ];

  return (
    <SMSPreviewContainer
      data-test={TEMPLATE_EDITOR_SMS_PREVIEW_PANE_ID}
      hasPreviewInfoMessage={isGlobalSmsEnabled}
      isPreviewWithData={isPreviewWithData}
    >
      <PreviewHeader />
      <PreviewContent
        className="template-editor-sms-preview-content"
        HeadContent={<HeadStyleMobile />}
        testId="template-editor-sms-preview-content"
      >
        <Preview selectedOs={selectedOs} selectedPreviewType={selectedPreviewType} />
      </PreviewContent>
      {isPreviewWithData ? (
        <PreviewRefreshButton
          onClick={() => onRefreshPreview()}
          variant={BUTTON_VARIANTS.SECONDARY}
          data-test={TEMPLATE_EDITOR_SMS_PREVIEW_REFRESH_BUTTON}
        >
          <AnimatedRefreshIcon $isAnimating={isRefreshing} />
        </PreviewRefreshButton>
      ) : (
        isGlobalSmsEnabled && (
          <div data-test="preview-info-message" style={previewInfoMessage}>
            <InfoFill height={12} width={12} isColorInherited />
            {displayContent.strOptOutInfoMessage}
          </div>
        )
      )}
      {shouldShowMMSLinkDisclaimer && (
        <div data-test="preview-info-message" style={previewInfoMessage}>
          <InfoFill height={12} width={12} isColorInherited />
          {displayContent.strMMSShortlinkMessage}
        </div>
      )}
      <PreviewFooter data-test="template-editor-sms-preview-footer">
        <PreviewControls>
          <SegmentedControl
            onSelect={setSelectedOs}
            optionMinWidth={spacing.double}
            options={osSelectOptions}
            value={selectedOs}
          />
          <StyledSelect
            data-test={TEMPLATE_EDITOR_SMS_PREVIEW_TYPE_SELECTOR}
            defaultValues={[selectedPreviewType]}
            id="template-editor-sms-preview-type-selector"
            isDisabled={isDisabled}
            onSelect={([newPreviewType]) => setSelectedPreviewType(newPreviewType as PreviewType)}
            options={previewTypeOptions(smsContactCardEnabled, !!previewContactCard)}
            size={BASIC_SELECT_SIZES.SMALL}
          />
          {isGlobalSmsEnabled && hasAlphanumericSupport && isPreviewWithData && (
            <SegmentedControl
              onSelect={setOptOutType}
              options={optOutTypeSelectOptions}
              value={optOutType}
            />
          )}
        </PreviewControls>
        <PreviewMessage>
          {hasDataParamsInMsg ? (
            <>
              <InfoFill height={12} width={12} isColorInherited />
              <span data-test={TEMPLATE_EDITOR_SMS_PREVIEW_DATA_MESSAGE}>
                {displayContent.dataParamsInMessage}
              </span>
            </>
          ) : (
            <div>
              <OptOutControls>
                <Toggle
                  isActive={optInMessageIsShown}
                  onClick={() => setOptInMessageIsShown(!optInMessageIsShown)}
                />
                <OptOutLabel>Opt-out instructions</OptOutLabel>
                <HelpTooltip
                  tooltipContent={PREVIEWER_CONTENT.optOutToggleTooltip}
                  iconProps={{ color: colors.brandPurple, height: 16, width: 16 }}
                  automaticallyAvoidCollisions={true}
                />
              </OptOutControls>
              <div>
                <span data-test={TEMPLATE_EDITOR_SMS_PREVIEW_CHAR_COUNT}>
                  {displayContent.strCharacterLengthDynamicMaxCount({
                    charCount: characterCount || 0,
                    maxCharCount: perMessageLimit,
                  })}
                </span>
                <Separator>•</Separator>
                <Tooltip
                  tooltipContent={PREVIEWER_CONTENT.characterCountTooltip}
                  isUnderlined={true}
                  automaticallyAvoidCollisions={true}
                >
                  <span data-test={TEMPLATE_EDITOR_SMS_PREVIEW_SEG_COUNT}>
                    {displayContent.strSegmentCount({ msgCount: segmentCount, typeOfSegment })}
                  </span>
                </Tooltip>
              </div>
            </div>
          )}
        </PreviewMessage>
      </PreviewFooter>
      {isSMSLinkShorteningEnabled && (
        <PreviewShortenAndTrackContainer data-test={TEMPLATE_EDITOR_SMS_PREVIEW_SHORTEN_TRACK}>
          {displayContent.shortenAndTrack}
        </PreviewShortenAndTrackContainer>
      )}
    </SMSPreviewContainer>
  );
};

export default PreviewPane;
